import { useEffect, useState } from 'react';

import { TFormalOffers } from '@helpers/FormalOffers/formalOffers.types';
import { getFormalOffersLatestOfferConfiguration } from '@helpers/FormalOffers/getFormalOffersLatestOfferConfiguration';
import { getFormalOffersMaxAvailableCash } from '@helpers/FormalOffers/getFormalOffersMaxAvailableCash';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { tracking } from '@iwoca/frontend-tracking-library';
import { fetchGetFormalOffersLatestOfferConfiguration } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { FundingProgressIndicator } from './FundingProgressIndicator/FundingProgressIndicator';
import { LoanSummary } from './LoanSummary/LoanSummary';
import { OfferConfiguration } from './OfferConfiguration/OfferConfiguration';
import { useStateKey } from '../../../../../../iwQueryClient/useStateKey';
import { TrustPilot } from '../../../../../TrustPilot/TrustPilot';
import { useSaveOfferStep } from '../../../../offerFlowMemory/useSaveOfferStep';
import { NeedHelpSection } from '../../../components/NeedHelpSection/NeedHelpSection';
import { OffersPageShell } from '../../components/OffersPageShell';

const getDefaultSelectedOfferId = (formalOffers: TFormalOffers) =>
    formalOffers.length === 1 ? formalOffers[0].offer_id : undefined;

export const FlexiOfferPage = ({
    formalOffers
}: {
    formalOffers: TFormalOffers;
}) => {
    const stateKey = useStateKey();
    const maxCashAmount = getFormalOffersMaxAvailableCash(formalOffers);

    const { data: latestOfferConfiguration } = useQuery({
        queryKey: ['fetchGetFormalOffersLatestOfferConfiguration', stateKey],
        queryFn: () =>
            fetchGetFormalOffersLatestOfferConfiguration({ stateKey }),
        select: (data) => data.data.latest_offer_configuration,
        suspense: true,
        cacheTime: 0
    });

    const { previouslySelectedFormalOffer, previouslySelectedAmount } =
        getFormalOffersLatestOfferConfiguration({
            latestOfferConfiguration,
            formalOffers
        });

    const [drawdownAmount, setDrawdownAmount] = useState(
        previouslySelectedAmount ?? maxCashAmount
    );
    const [selectedOfferId, setSelectedOfferId] = useState(
        previouslySelectedFormalOffer?.offer_id ??
            getDefaultSelectedOfferId(formalOffers)
    );

    const saveOfferStep = useSaveOfferStep();

    useEffect(() => {
        tracking.ampli.approvedViewVisited({
            status: 'flexi',
            offers_page_version: '2.0'
        });
    }, []);

    const getSelectedOffer = (offerId: string | undefined) =>
        formalOffers.find((offer) => offer.offer_id === offerId);

    const selectedOffer = getSelectedOffer(selectedOfferId);

    useEffect(() => {
        if (!selectedOffer) return;
        if (selectedOffer.offer_id === previouslySelectedFormalOffer?.offer_id)
            return;

        saveOfferStep({
            formalOffer: selectedOffer,
            step: 'offer',
            amount: drawdownAmount
        });
        // Intentionally omitting drawdownAmount — saving on every slider tick or input keystroke would spam the API.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOffer, saveOfferStep]);

    const handleDrawdownAmountChange = (amount: number) => {
        setDrawdownAmount(amount);
        tracking.ampli.amountSliderUsed({
            offer_id: selectedOfferId,
            'Slider amount': amount
        });
    };

    const handleSelectOffer = (offerId: string) => {
        const offer = getSelectedOffer(offerId);
        setSelectedOfferId(offerId);

        if (offer) {
            tracking.ampli.formalOfferSelected({
                formal_offer_id: offerId,
                duration: getDurationInMonths(offer.duration!)
            });
        }
    };

    return (
        <OffersPageShell
            progressIndicator={<FundingProgressIndicator />}
            mainContent={
                <div className="space-y-2xl min-m:pb-5xl">
                    <OfferConfiguration
                        formalOffers={formalOffers}
                        drawdownAmount={drawdownAmount}
                        onDrawdownAmountChange={handleDrawdownAmountChange}
                        selectedOffer={selectedOffer}
                        onSelectOffer={handleSelectOffer}
                    />

                    <div
                        className="px-l min-sm:px-xl min-m:hidden"
                        data-testid="need-help-mobile"
                    >
                        <NeedHelpSection variant="offers-mobile" />
                    </div>

                    <div className="px-l pb-m min-sm:px-xl">
                        <TrustPilot />
                    </div>
                </div>
            }
            sidePanel={
                <LoanSummary
                    selectedOffer={selectedOffer}
                    drawdownAmount={drawdownAmount}
                />
            }
        />
    );
};
